import { Formik } from 'formik';
import React, { useEffect, useState, useContext, useRef } from 'react';
import { ROUTES_PATH, VALIDATOR_PATTERN } from '../../constants';
import dayjs from 'dayjs';
import Select from 'react-select';
import AppConfigService from '../../services/AppConfig';
import { useSelectTypeahead } from '../../utils/selectTypeaheadHelper';
import { Customer } from '../../interfaces/Customer';
import { useCustomer } from './../../contexts/CustomerContext/customer-context.hook';
import { useNavigate } from 'react-router-dom';
import { CustomerContext } from '../../contexts/CustomerContext/customer-context';
import {
  FormFloating,
  FormInputLabel,
  ValidationError,
  FormInput,
  FormInputRow,
  PersonalDataSelect,
} from '../../styles/FormStyledComponents';
import {
  MainButton,
  MainButtonSection,
  Title,
} from '../../styles/GlobalStyledComponents';
import { FormElem } from './StyledComponent';

import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  checkNie,
  validateBirthDate,
  validateEmail,
  validateName,
  validatePhone,
  validateSurname,
} from '../../utils/validators';
import { KeyModel } from '../../services/AppConfig/types';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import { useLoanSimulation } from '../../contexts/LoanSimulationContext';
import { useProduct } from '../../contexts/ProductContext';
import { CreditProposal } from '../../interfaces/CreditProposal';
import CreditProposalService from '../../services/CreditProposal';
import InsightService from '../../services/Insight';
import { useCustomerClassification } from '../../contexts/CustomerClassificationContext';
import { DecisionStatus } from '../../interfaces/DecisionEngine';
import {
  CustomerClassification,
  CustomerClassificationEnum,
} from '../../interfaces/CustomerClassification';
import {
  CreditProposalFlowStep,
  CreditProposalResult,
} from '../../interfaces/CreditProposalResult';
import {
  blockCharactersNoDecimal,
} from '../../utils/formInputHelper';
import { useMerchant } from '../../contexts/MerchantContext';
import PostMessageService from '../../services/PostMessage';
import AnalyticsService from '../../services/Analytics';
import { ContinuePDataModel, DenialStep } from '../../interfaces/Analytics';
import AnalyticsHelper from '../../utils/analyticsHelper';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  identifier: string;
  birthdate: string;
  country: string;
  streetType: string;
  address: string;
  addressNumber: string;
  addressAux: string;
  postalCode: string;
  city: string;
  province: string;
  nationality: string;
}

interface PersonalDataProps {
  loadingEvent: (data: boolean) => void;
}

export const PersonalData: React.FC<PersonalDataProps> = ({ loadingEvent }) => {
  let navigate = useNavigate();
  const { createCustomer } = useCustomer();
  const { isPostDataMessages } = useMerchant();
  const { product, amount } = useProduct();
  const { loanSimulation } = useLoanSimulation();
  const { creditProposalResult, createCreditProposalResult } =
    useCreditProposalResult();
  const { customerClassification, createCustomerClassification } =
    useCustomerClassification();
  const { customer } = useContext(CustomerContext);
  const [isNie, setIsNie] = useState<boolean>(false);
  const [countryList, setCountryList] = useState<KeyModel[]>();
  const [nieCountryList, setNieCountryList] = useState<KeyModel[]>();
  const [surname1, setSurname1] = useState<string | null>(null);
  const [surname2, setSurname2] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [flowSus, setFlowSus] = useState<boolean>(false);
  const selectCountryRef: any = useRef<HTMLSelectElement>(null);
  const selectNationalityRef: any = useRef<HTMLSelectElement>(null);
  const countryTypeahead = useSelectTypeahead();
  const nationalityTypeahead = useSelectTypeahead();

  const storedCustomerParams = sessionStorage.getItem('customerData')
    ? JSON.parse(sessionStorage.getItem('customerData')!)
    : null;
  const inputMode = sessionStorage.getItem('inputMode')
    ? JSON.parse(sessionStorage.getItem('inputMode')!)
    : null;

  const startTime = dayjs();

  const mockInitialValues = {
    firstName: 'John',
    lastName: 'Doe Doe',
    email: 'test@test.com',
    phone: '677777771',
    // Belender bad phone
    // phone: '689792000',
    identifier: '55787674R',
    // require_enrichment_personal_finances: true
    // identifier: '45962987V',
    // Belender failed with retry
    //identifier: '96056421A',
    birthdate: '25/06/1990',
    country: 'ES',
    streetType: '',
    address: '',
    addressNumber: '',
    addressAux: '',
    postalCode: '',
    city: '',
    province: '',
    nationality: 'ES',
  };

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    identifier: '',
    birthdate: '',
    country: isNie && nieCountryList ? nieCountryList[0].code : 'ES',
    streetType: '',
    address: '',
    addressNumber: '',
    addressAux: '',
    postalCode: '',
    city: '',
    province: '',
    nationality: 'ES',
  };

  const [initVal, setInitVal] = useState(initialValues);

  dayjs.extend(customParseFormat);
  dayjs.extend(isBetween);

  const surnameValidation = (value: string) => {
    const validationResp = validateSurname(value, isNie);
    if (!validationResp.error) {
      setSurname1(validationResp.surnameOne!);
      setSurname2(validationResp.surnameTwo!);
    }

    return validationResp.error;
  };

  const handlePhoneInput = (e: any, handleChange: any) => {
    const phoneValue = e.target.value;
    if (phoneValue.length <= 9) {
      handleChange(e);
    }
  };

  const validateIdentifier = (value: string) => {
    let error: string | undefined;
    let charIndex: number;
    let valid: boolean;
    if (!value) {
      error = 'Añade DNI/NIE';
    } else {
      const letter = value.substr(-1).toUpperCase();

      const isNieD = checkNie(value);
      setIsNie(isNieD);

      const nie = isNieD
        ? value
          .replace(/^[xX]/, '0')
          .replace(/^[yY]/, '1')
          .replace(/^[zZ]/, '2')
        : value;

      const dniNumbers = nie.substring(0, 8);

      //Se valida que los 8 primero caracteres son numéricos
      if (!/^\d{8}$/.test(dniNumbers)) {
        return 'DNI/NIE incorrecto';
      }

      //Cálculo del índice correspondiente con la letra que llevará asignada el DNI/NIE
      charIndex = parseInt(dniNumbers) % 23;

      //Validamos por un lado el patrón de NIE o DNI y, a continuación, si coincide la letra final
      valid =
        (isNieD
          ? VALIDATOR_PATTERN.NIE.test(value)
          : VALIDATOR_PATTERN.DNI.test(value)) &&
        VALIDATOR_PATTERN.DNI_CHARS.charAt(charIndex) === letter;

      if (!valid) {
        error = 'DNI/NIE incorrecto';
      }
    }
    return error;
  };

  const getDecision = async (
    creditProposalData: CreditProposalResult,
    counter: number,
    classification: CustomerClassification,
  ) => {
    counter++;
    try {
      const decisionResp = await CreditProposalService.getDecision(
        creditProposalData.credit_proposal_id,
      );
      switch (decisionResp.decision) {
        case DecisionStatus.APPROVED:
          // startWebSocket();
          if (classification?.require_enrichment_personal_finances) {
            createCreditProposalResult({
              ...creditProposalData,
              flow_step: CreditProposalFlowStep.PERSONAL_FINANCES,
            } as CreditProposalResult);
          } else if (creditProposalData?.product === 'PLS') {
            createCreditProposalResult({
              ...creditProposalData,
              flow_step: CreditProposalFlowStep.BANK_READER,
            } as CreditProposalResult);
          } else if (
            classification?.require_documentation &&
            !creditProposalData.ocr_id
          ) {
            createCreditProposalResult({
              ...creditProposalData,
              flow_step: CreditProposalFlowStep.OCR,
            } as CreditProposalResult);
          } else {
            createCreditProposalResult({
              ...creditProposalData,
              flow_step: CreditProposalFlowStep.PAYMENT,
            } as CreditProposalResult);
          }
          navigate(ROUTES_PATH.OPERATION_SHIPPING);
          break;
        case DecisionStatus.ALTERNATIVE_OFFER:
        case DecisionStatus.COUNTER_OFFER: // Contraoferta
          if (
            decisionResp.counter_offers &&
            decisionResp.counter_offers.length > 0
          ) {
            createCreditProposalResult({
              ...creditProposalData,
              counter_offers: decisionResp.counter_offers,
              flow_step: CreditProposalFlowStep.COUNTER_OFFER,
            } as CreditProposalResult);
          }
          navigate(ROUTES_PATH.COUNTER_OFFER);
          break;
        case DecisionStatus.DENIED:
          AnalyticsService.sendLoanRefused({ denial_step: DenialStep.DEC });
          navigate(ROUTES_PATH.DENIAL);
          break;
        case DecisionStatus.MISSING_DATA:
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
          break;
        default:
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
          break;
      }
      loadingEvent(false);
    } catch (error) {
      if (`${error}`.includes('409') && counter <= 11) {
        setTimeout(() => {
          getDecision(creditProposalData, counter, classification);
        }, 3000);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
        loadingEvent(false);
      }
    }
  };

  const createCreditProposal = async (customer: Customer) => {
    delete customer['address'];
    delete customer['job'];
    const data = {
      commercial_offer_id: loanSimulation?.commercial_offer_id,
      product: loanSimulation?.flow,
      term: loanSimulation?.term,
      customer: customer,
      requested_amount:
        amount ??
        (loanSimulation &&
          loanSimulation?.total_cost - loanSimulation?.opening_fee),
      purchase_amount:
        amount ??
        (loanSimulation &&
          loanSimulation?.total_cost - loanSimulation?.opening_fee),
      good_type: product?.code ?? sessionStorage.getItem('CEX-GoodType'),
    } as CreditProposal;

    try {
      loadingEvent(true);
      const response = await CreditProposalService.create(data);
      createCreditProposalResult(response);

      if (response && isPostDataMessages) {
        PostMessageService.ProposalCreated(response.credit_proposal_id);
      }

      InsightService.logTrace('CreditProposal', [
        { creditProposalId: response.credit_proposal_id },
      ]);
      const classification = await CreditProposalService.postClassification(
        response.credit_proposal_id,
        data,
      );

      const analyticsCustomerData: ContinuePDataModel = {
        cust_age_segment: AnalyticsHelper.getAgeRange(customer.birth_date),
        cust_birth_country: customer.birth_country,
        cust_nationality: customer.nationality,
        cust_email_domain: customer.email,
        cust_classification: classification.customer_classification,
        prop_require_documentation: classification.require_documentation,
        prop_require_labor_data: classification.require_labor_data,
        prop_require_secure_payment: classification.require_secure_payment,
        prop_require_require_address_data: classification.require_address_data,
        prop_require_enrichment_personal_finances:
          classification.require_enrichment_personal_finances,
        prop_source_application: response.source_application,
        prop_product: response.product,
        prop_amount: response.loan_amount,
        time_elapsed: AnalyticsHelper.getTimeElapse({
          initTime: startTime,
          finishTime: dayjs(),
        }),
        step_number: 2,
      };

      if (flowSus)
        analyticsCustomerData.prop_refinanced_amount =
          response.requested_amount;

      AnalyticsService.continuePdata(analyticsCustomerData);
      createCustomerClassification(classification);
      if (
        classification.customer_classification ===
        CustomerClassificationEnum.denied
      ) {
        AnalyticsService.sendLoanRefused({ denial_step: DenialStep.CUST });
        navigate(ROUTES_PATH.DENIAL);
        loadingEvent(false);
      } else if (
        classification.require_labor_data ||
        classification.require_address_data
      ) {
        navigate(ROUTES_PATH.LABOR_DATA);
        loadingEvent(false);
      } else {
        await getDecision(response, 0, classification);
      }
      // loadingEvent(false);
    } catch (error: any) {
      loadingEvent(false);
      if (
        error &&
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0081',
        )
      ) {
        navigate(ROUTES_PATH.EXISTING_RFN);
      } else if (
        error &&
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0089',
        )
      ) {
        navigate(ROUTES_PATH.PERSONAL_FINANCES_UNAVAILABLE, {
          state: { errorType: 'DRF0089' },
        });
      } else if (
        error &&
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0092',
        )
      ) {
        //2º caso de error de Belender (jubilados)
        navigate(ROUTES_PATH.PERSONAL_FINANCES_UNAVAILABLE, {
          state: { errorType: 'DRF0092' },
        });
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const handleContinue = (value: FormValues) => {
    loadingEvent(true);
    if (!flowSus || !creditProposalResult) {
      const customerData = getCustomerFromPersonalDataForm(value);
      createCustomer(customerData);
      // CREATE CREDIT PROPOSAL
      createCreditProposal(customerData);
    } else if (customerClassification) {
      if (
        customerClassification.require_address_data ||
        customerClassification.require_labor_data
      ) {
        navigate(ROUTES_PATH.LABOR_DATA);
      } else {
        creditProposalResult &&
          getDecision(creditProposalResult, 0, customerClassification);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getCountryList = async () => {
    const data = await AppConfigService.getCountryData();
    const nieCountries = data[0].keys.filter((x) => x.filters.includes('NE'));
    setNieCountryList(nieCountries);
    setCountryList(data[0].keys);
    return nieCountries;
  };

  const getCustomerFromPersonalDataForm = (value: FormValues) => {
    const data: Customer = {
      document_id: value.identifier.toLocaleUpperCase(),
      name: value.firstName,
      surname1: surname1,
      surname2: surname2,
      email: value.email,
      nationality: value.nationality || '',
      birth_country: value.country,
      birth_date: dayjs(value.birthdate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      phones: [value.phone.toString()],
      job: {
        contract_type:
          customer?.job?.contract_type ||
          creditProposalResult?.customer?.job?.contract_type ||
          '',
        job_type:
          customer?.job?.job_type ||
          creditProposalResult?.customer?.job?.job_type ||
          '',
        monthly_income:
          customer?.job?.monthly_income ||
          creditProposalResult?.customer?.job?.monthly_income ||
          '',
      },
      address: {
        road_type:
          customer?.address?.road_type ||
          creditProposalResult?.customer?.address?.road_type ||
          '',
        address:
          customer?.address?.address ||
          creditProposalResult?.customer?.address?.address ||
          '',
        number:
          customer?.address?.number ||
          creditProposalResult?.customer?.address?.number ||
          '',
        province:
          customer?.address?.province ||
          creditProposalResult?.customer?.address?.province ||
          '',
        postal_code:
          customer?.address?.postal_code ||
          creditProposalResult?.customer?.address?.postal_code ||
          '',
        city:
          customer?.address?.city ||
          creditProposalResult?.customer?.address?.city ||
          '',
        aux:
          customer?.address?.aux ||
          creditProposalResult?.customer?.address?.aux ||
          '',
      },
    };
    return data;
  };

  useEffect(() => {
    loadingEvent(true);
    void initPersonalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initPersonalData = async () => {
    const nieCountries = await getCountryList();
    if (creditProposalResult && creditProposalResult.customer) {
      const lastNameObj =
        creditProposalResult.customer.surname1! +
        ' ' +
        creditProposalResult.customer.surname2!;
      const creditPropValues = {
        firstName: creditProposalResult.customer.name,
        lastName: lastNameObj,
        email: creditProposalResult.customer.email
          ? creditProposalResult.customer.email
          : '',
        phone: creditProposalResult.customer.phones[0],
        identifier: creditProposalResult.customer.document_id,
        birthdate: dayjs(
          creditProposalResult.customer.birth_date,
          'YYYY-MM-DD',
        ).format('DD/MM/YYYY'),
        country: creditProposalResult.customer.birth_country,
        streetType: '',
        address: '',
        addressNumber: '',
        addressAux: '',
        postalCode: '',
        city: '',
        province: '',
        nationality: creditProposalResult.customer.nationality,
      };
      setInitVal(creditPropValues);
    }
    if (customer) {
      const newObject = {
        firstName: customer?.name,
        lastName: customer?.surname1 + ' ' + customer?.surname2,
        email: customer?.email ? customer?.email : '',
        phone: customer?.phones[0],
        identifier: customer?.document_id,
        birthdate: dayjs(customer?.birth_date, 'YYYY-MM-DD').format(
          'DD/MM/YYYY',
        ),
        country: customer?.birth_country,
        nationality: customer?.nationality,
        streetType: '',
        address: '',
        addressNumber: '',
        addressAux: '',
        postalCode: '',
        city: '',
        province: '',
      };
      setInitVal(newObject);
    } else if (storedCustomerParams) {
      const storedDocumentId = storedCustomerParams?.document_id ?? '';
      const isNieDocument = storedDocumentId
        ? checkNie(storedDocumentId)
        : false;
      // El NIE es de extranjeros: 'ES' no es un país de nacimiento válido.
      // Igual que en el flujo manual, se usa el primer país de la lista NIE.
      const defaultCountry =
        isNieDocument && nieCountries.length ? nieCountries[0].code : 'ES';
      setIsNie(isNieDocument);
      const newObject = {
        firstName: storedCustomerParams?.name ?? '',
        lastName: storedCustomerParams?.surname ?? '',
        email: storedCustomerParams?.email ?? '',
        phone: storedCustomerParams?.phone ?? '',
        identifier: storedDocumentId,
        birthdate: storedCustomerParams?.birth_date
          ? dayjs(storedCustomerParams?.birth_date, 'YYYY-MM-DD').format(
            'DD/MM/YYYY',
          )
          : '',
        country: defaultCountry,
        nationality: defaultCountry,
        streetType: '',
        address: '',
        addressNumber: '',
        addressAux: '',
        postalCode: '',
        city: '',
        province: '',
      };
      setInitVal(newObject);
    }
    sessionStorage.getItem('Flow-SUS') === 'true'
      ? setFlowSus(true)
      : setFlowSus(false);
    loadingEvent(false);
  };

  const handleFocus = (event: any) => {
    if (event.type === 'focus') {
      setActiveField(event.target.name);
    } else if (event.type === 'blur') {
      setActiveField(null);
    }
  };

  let currentDateValue: string = '';

  const handleDate = (e: React.ChangeEvent<any>) => {
    const dateValue = e.target.value
      .replace(/\D/g, '')
      .match(/(\d{0,2})(\d{0,2})(\d{0,4})/);

    const isDelete = currentDateValue === e.target.value + '/' ? true : false;

    let transformedValue;
    if (!dateValue[2]) {
      transformedValue =
        dateValue[1].length > 1 && !isDelete
          ? `${dateValue[1]}/`
          : dateValue[1];
    } else if (dateValue[2] && !dateValue[3]) {
      transformedValue =
        dateValue[2].length > 1 && !isDelete
          ? `${dateValue[1]}/${dateValue[2]}/`
          : `${dateValue[1]}/${dateValue[2]}`;
    } else {
      transformedValue = `${dateValue[1]}/${dateValue[2]}/${dateValue[3]}`;
    }

    e.target.value = transformedValue;
    e.target.value.replace(/\D/g, '');
    currentDateValue = e.target.value;
  };

  return (
    <>
      {/* <Title id="personal-data-title">Datos personales</Title> */}
      {import.meta.env.VITE_ENV === 'development' && (
        <button onClick={() => setInitVal(mockInitialValues)}>
          Init value
        </button>
      )}
      <Title id="personal-data-title">Datos personales</Title>
      <Formik
        onSubmit={handleContinue}
        initialValues={initVal}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          handleChange,
        }) => (
          <FormElem>
            <FormFloating>
              <FormInput
                id="personal-data-name-input"
                name="firstName"
                autoComplete="off"
                validate={validateName}
                error={errors.firstName && touched.firstName ? 'error' : ''}
                value={values.firstName}
                onFocus={handleFocus}
                onBlur={(e: any) => {
                  handleFocus(e);
                  handleBlur(e);
                }}
                readOnly={
                  (flowSus && creditProposalResult) ||
                  (storedCustomerParams?.name &&
                    inputMode === 'customer_blocked' &&
                    'readonly')
                }
                isDisabled={
                  !!(flowSus && creditProposalResult) ||
                  (storedCustomerParams?.name &&
                    inputMode === 'customer_blocked')
                }
              />
              <FormInputLabel
                htmlFor="firstName"
                $error={errors.firstName && touched.firstName ? 'error' : ''}
                $emptyvalue={
                  values.firstName || activeField === 'firstName' ? '' : 'true'
                }
              >
                Nombre
              </FormInputLabel>
              {errors.firstName && touched.firstName && (
                <ValidationError>{errors.firstName}</ValidationError>
              )}
            </FormFloating>
            <FormFloating>
              <FormInput
                id="personal-data-surname-input"
                autoComplete="off"
                name="lastName"
                validate={surnameValidation}
                error={errors.lastName && touched.lastName ? 'error' : ''}
                onFocus={handleFocus}
                onBlur={(e: any) => {
                  handleFocus(e);
                  handleBlur(e);
                }}
                readOnly={
                  (flowSus && creditProposalResult) ||
                  (storedCustomerParams?.surname &&
                    inputMode === 'customer_blocked' &&
                    'readonly')
                }
                isDisabled={
                  !!(flowSus && creditProposalResult) ||
                  (storedCustomerParams?.surname &&
                    inputMode === 'customer_blocked')
                }
              />
              <FormInputLabel
                htmlFor="lastName"
                $error={errors.lastName && touched.lastName ? 'error' : ''}
                $emptyvalue={
                  values.lastName || activeField === 'lastName' ? '' : 'true'
                }
              >
                Apellidos
              </FormInputLabel>
              {errors.lastName && touched.lastName && (
                <ValidationError>{errors.lastName}</ValidationError>
              )}
            </FormFloating>
            <FormFloating>
              <FormInput
                id="personal-data-email-input"
                autoComplete="off"
                name="email"
                type="email"
                validate={validateEmail}
                error={errors.email && touched.email ? 'error' : ''}
                onFocus={handleFocus}
                onBlur={(e: any) => {
                  handleFocus(e);
                  handleBlur(e);
                }}
                readOnly={
                  (flowSus && creditProposalResult) ||
                  (storedCustomerParams?.email &&
                    inputMode === 'customer_blocked' &&
                    'readonly')
                }
                isDisabled={
                  !!(flowSus && creditProposalResult) ||
                  (storedCustomerParams?.email &&
                    inputMode === 'customer_blocked')
                }
              />
              <FormInputLabel
                htmlFor="email"
                $error={errors.email && touched.email ? 'error' : ''}
                $emptyvalue={
                  values.email || activeField === 'email' ? '' : 'true'
                }
              >
                Email
              </FormInputLabel>
              {errors.email && touched.email && (
                <ValidationError>{errors.email}</ValidationError>
              )}
            </FormFloating>
            <FormInputRow>
              <FormFloating>
                <FormInput
                  id="personal-data-phone-input"
                  autoComplete="off"
                  name="phone"
                  type="number"
                  maxLength="9"
                  inputMode="numeric"
                  validate={validatePhone}
                  onKeyDown={blockCharactersNoDecimal}
                  error={errors.phone && touched.phone ? 'error' : ''}
                  onFocus={handleFocus}
                  onBlur={(e: any) => {
                    handleFocus(e);
                    handleBlur(e);
                  }}
                  onChange={(e: any) => {
                    handlePhoneInput(e, handleChange);
                  }}
                  readOnly={
                    (flowSus && creditProposalResult) ||
                    (storedCustomerParams?.phone &&
                      inputMode === 'customer_blocked' &&
                      'readonly')
                  }
                  isDisabled={
                    !!(flowSus && creditProposalResult) ||
                    (storedCustomerParams?.phone &&
                      inputMode === 'customer_blocked')
                  }
                />
                <FormInputLabel
                  htmlFor="phone"
                  $error={errors.phone && touched.phone ? 'error' : ''}
                  $emptyvalue={
                    values.phone || activeField === 'phone' ? '' : 'true'
                  }
                >
                  Teléfono
                </FormInputLabel>
                {errors.phone && touched.phone && (
                  <ValidationError>{errors.phone}</ValidationError>
                )}
              </FormFloating>
              <FormFloating>
                <FormInput
                  id="personal-data-identifier-input"
                  autoComplete="off"
                  name="identifier"
                  maxLength="9"
                  validate={validateIdentifier}
                  error={errors.identifier && touched.identifier ? 'error' : ''}
                  onFocus={handleFocus}
                  onBlur={(e: any) => {
                    handleFocus(e);
                    handleBlur(e);
                    if (isNie && nieCountryList && values.country === 'ES') {
                      setFieldValue('country', nieCountryList[0].code);
                      setFieldValue('nationality', nieCountryList[0].code);
                    }
                  }}
                  readOnly={
                    (flowSus && creditProposalResult) ||
                    (storedCustomerParams?.document_id &&
                      inputMode === 'customer_blocked' &&
                      'readonly')
                  }
                  isDisabled={
                    !!(flowSus && creditProposalResult) ||
                    (storedCustomerParams?.document_id &&
                      inputMode === 'customer_blocked')
                  }
                />
                <FormInputLabel
                  htmlFor="identifier"
                  $error={
                    errors.identifier && touched.identifier ? 'error' : ''
                  }
                  $emptyvalue={
                    values.identifier || activeField === 'identifier'
                      ? ''
                      : 'true'
                  }
                >
                  DNI o NIE
                </FormInputLabel>
                {errors.identifier && touched.identifier && (
                  <ValidationError>{errors.identifier}</ValidationError>
                )}
              </FormFloating>
            </FormInputRow>
            <FormInputRow>
              <FormFloating>
                <FormInput
                  id="personal-data-birthdate-input"
                  name="birthdate"
                  autoComplete="off"
                  validate={validateBirthDate}
                  onFocus={(e: React.ChangeEvent<any>) => {
                    const form = e.target.form;
                    const index = [...form].indexOf(e.target);
                    form.elements[index].select();
                    handleFocus(e);
                  }}
                  onBlur={(e: any) => {
                    handleFocus(e);
                    handleBlur(e);
                  }}
                  identifier={isNie ? 'nie' : ''}
                  error={errors.birthdate && touched.birthdate ? 'error' : ''}
                  onKeyUp={handleDate}
                  readOnly={
                    (flowSus && creditProposalResult) ||
                    (storedCustomerParams?.birth_date &&
                      inputMode === 'customer_blocked' &&
                      'readonly')
                  }
                  isDisabled={
                    !!(flowSus && creditProposalResult) ||
                    (storedCustomerParams?.birth_date &&
                      inputMode === 'customer_blocked')
                  }
                />
                <FormInputLabel
                  htmlFor="birthdate"
                  $error={errors.birthdate && touched.birthdate ? 'error' : ''}
                  $emptyvalue={
                    values.birthdate || activeField === 'birthdate'
                      ? ''
                      : 'true'
                  }
                >
                  Fecha de nacimiento
                </FormInputLabel>
                {errors.birthdate && touched.birthdate && (
                  <ValidationError>{errors.birthdate}</ValidationError>
                )}
              </FormFloating>
              <PersonalDataSelect
                onClick={() => selectCountryRef.current?.focus()}
              >
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable={false}
                  isSearchable={false}
                  placeholder={null}
                  options={isNie ? nieCountryList : countryList}
                  openMenuOnFocus={true}
                  isDisabled={flowSus}
                  {...countryTypeahead(
                    isNie ? nieCountryList : countryList,
                    values.country,
                    (code) => setFieldValue('country', code),
                  )}
                  // menuIsOpen={true}
                  name="country"
                  id="personal-data-country-input"
                  ref={selectCountryRef}
                  formatOptionLabel={(v) => v.description_code}
                  getOptionLabel={(v) => v.description_code}
                  getOptionValue={(v) => v.code}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: '#dfe7ef !important',
                      height: 'calc(3.5rem + 2px);',
                      color: '#333030 !important',
                      backgroundColor: '#fafafa',
                      outlineColor: 'none',
                      boxShadow: 'none',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: isNie ? '0' : '0 0 6px 0',
                      padding: '1.0rem 0.1rem 0.625rem',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      fontSize: '16px',
                      fontWeight: '400',
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: '#dfe7ef',
                      boxShadow: 'none',
                      color: '#333030 !important',
                    }),
                  }}
                  value={(isNie ? nieCountryList : countryList)?.find(
                    (op) => op.code === values.country,
                  )}
                />
                {/* // )} */}
                <FormInputLabel
                  htmlFor="country"
                  $error={errors.country && touched.country ? 'error' : ''}
                >
                  País de nacimiento
                </FormInputLabel>
              </PersonalDataSelect>
            </FormInputRow>
            {/* <button type="button" onClick={() => {
              if (selectRef.current) {
                selectRef.current.focus();
              }
              }}>test</button> */}
            {isNie && (
              <FormFloating
                onClick={() => selectNationalityRef.current?.focus()}
              >
                {/* <FormInput
                  component="select"
                  id="personal-data-nationality-input"
                  autoComplete="off"
                  name="nationality"
                  validate={validateNationality}
                  identifier={isNie ? "nie" : ""}
                >
                  {(isNie ? nieCountryList : countryList)?.map(
                    (country: KeyModel) => (
                      <FormOption key={country.code} value={country.code}>
                        {country.description_code}
                      </FormOption>
                    )
                  )}
                </FormInput> */}

                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable={false}
                  isSearchable={false}
                  placeholder={null}
                  options={isNie ? nieCountryList : countryList}
                  openMenuOnFocus={true}
                  isDisabled={flowSus}
                  {...nationalityTypeahead(
                    isNie ? nieCountryList : countryList,
                    values.nationality,
                    (code) => setFieldValue('nationality', code),
                  )}
                  // menuIsOpen={true}
                  name="nationality"
                  id="personal-data-nationality-input"
                  ref={selectNationalityRef}
                  formatOptionLabel={(v) => v.description_code}
                  getOptionLabel={(v) => v.description_code}
                  getOptionValue={(v) => v.code}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: '#dfe7ef !important',
                      height: 'calc(3.5rem + 2px);',
                      color: '#333030',
                      backgroundColor: '#fafafa',
                      outlineColor: 'none',
                      boxShadow: 'none',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: isNie ? '0' : '0 0 6px 0',
                      padding: '1.0rem 0.1rem 0.625rem',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      fontSize: '16px',
                      fontWeight: '400',
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: '#dfe7ef',
                      boxShadow: 'none',
                    }),
                  }}
                  value={(isNie ? nieCountryList : countryList)?.find(
                    (op) => op.code === values.nationality,
                  )}
                />
                <FormInputLabel
                  htmlFor="nationality"
                  $error={
                    errors.nationality && touched.nationality ? 'error' : ''
                  }
                >
                  Nacionalidad
                </FormInputLabel>
                {errors.nationality && touched.nationality && (
                  <ValidationError>{errors.nationality}</ValidationError>
                )}
              </FormFloating>
            )}
            <MainButtonSection>
              <MainButton id="personal-data-continue-btn" type="submit">
                Continuar
              </MainButton>
            </MainButtonSection>
          </FormElem>
        )}
      </Formik>
    </>
  );
};
